import { store } from './store.js';
// ========================================
// PASO 2: SUBRAYADO INTERACTIVO
// ========================================
import { wizardState, getString, getProblemStatement } from './wizardState.js';
import { safeSetHTML } from './security.js';

// Text parser helper: Divide el texto en "burbujas" o frases grandes
function parseTextIntoSegments(text) {
    if (!text) return [];

    const segments = [];
    // Dividir por signos de puntuación o conjunciones para separar los datos
    const parts = text.split(/([.,?!;]+|\s+y\s+|\s+e\s+|\s+o\s+|\s+u\s+|\s+pero\s+)/ig);
    
    let currentSegment = "";
    for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (!part) continue;
        
        if (/^[.,?!;]+$/.test(part)) {
            currentSegment += part;
            segments.push(currentSegment);
            currentSegment = "";
        } else if (/^\s+(y|e|o|u|pero)\s+$/i.test(part)) {
            if (currentSegment.trim()) {
                segments.push(currentSegment);
            }
            currentSegment = part;
        } else {
            currentSegment += part;
        }
    }
    if (currentSegment.trim()) {
        segments.push(currentSegment);
    }

    const finalSegments = [];
    segments.forEach(chunk => {
        if (!chunk.trim()) return;
        if (chunk.includes('¿') || chunk.includes('?')) {
            finalSegments.push({ text: chunk, type: 'question' });
        } else {
            finalSegments.push({ text: chunk, type: 'text' });
        }
    });

    return finalSegments;
}

export function initializeHighlighting() {
    if (!store.state || !store.state.problems) return;

    const problem = store.state.problems[store.state.currentProblem];
    const statementText = getProblemStatement(problem);

    // Dividir en segmentos semánticos
    const segments = parseTextIntoSegments(statementText);
    const highlightableDiv = document.getElementById('highlightableText');

    if (!highlightableDiv) return;

    safeSetHTML(highlightableDiv, '');

    segments.forEach((segment, index) => {
        const span = document.createElement('span');
        span.textContent = segment.text;
        span.dataset.segmentIndex = index;
        span.dataset.segmentType = segment.type; // 'question', 'data', 'text'
        span.className = 'highlight-segment';
        span.style.color = '#000000'; // FORCE BLACK TEXT for visibility
        span.style.fontWeight = '600';
        span.addEventListener('click', () => highlightSegment(index));
        highlightableDiv.appendChild(span);
    });

    // Cargar imagen en el recuadro del Paso 2
    const imgContainer = document.getElementById('problemImageContainerStep2');
    const imgEl = document.getElementById('problemImageStep2');
    if (imgContainer && imgEl && problem.images && problem.images.length > 0) {
        imgEl.src = problem.images[0];
        imgContainer.style.display = 'block';
    } else if (imgContainer) {
        imgContainer.style.display = 'none';
    }

    // Ocultar texto principal del Paso 1 para enfocarse en la mesa de trabajo
    const mainProblemContainer = document.getElementById('problemTextContainer');
    if (mainProblemContainer) {
        mainProblemContainer.style.display = 'none';
    }

    if (window.Logger) {
        Logger.info('📝 Texto dividido en', segments.length, 'segmentos semánticos');
    }
}

export function setHighlightMode(mode) {
    wizardState.currentHighlightMode = mode;

    // Actualizar estados visuales de los botones
    const questionBtn = document.getElementById('questionModeBtn');
    const usefulBtn = document.getElementById('usefulModeBtn');

    if (questionBtn && usefulBtn) {
        questionBtn.classList.remove('active');
        usefulBtn.classList.remove('active');

        if (mode === 'question') {
            questionBtn.classList.add('active');
        } else if (mode === 'useful') {
            usefulBtn.classList.add('active');
        }
    }
}

export function highlightSegment(segmentIndex) {
    const mode = wizardState.currentHighlightMode;
    if (!mode) return;

    const span = document.querySelector(`.highlight-segment[data-segment-index="${segmentIndex}"]`);
    if (!span) return;

    // Toggle o cambiar resaltado
    if (span.classList.contains(`highlight-${mode}`)) {
        span.classList.remove(`highlight-${mode}`);
        // Limpiar del estado
        const idxQ = wizardState.data.highlighting.question.indexOf(segmentIndex);
        if (idxQ > -1) wizardState.data.highlighting.question.splice(idxQ, 1);

        const idxU = wizardState.data.highlighting.useful.indexOf(segmentIndex);
        if (idxU > -1) {
            wizardState.data.highlighting.useful.splice(idxU, 1);
            wizardState.data.usefulDataTexts.splice(idxU, 1);
        }
    } else {
        // Si ya tenía otro resaltado, quitarlo
        span.classList.remove('highlight-question', 'highlight-useful');

        // Quitar de otros arrays de estado
        const idxQ = wizardState.data.highlighting.question.indexOf(segmentIndex);
        if (idxQ > -1) wizardState.data.highlighting.question.splice(idxQ, 1);

        const idxU = wizardState.data.highlighting.useful.indexOf(segmentIndex);
        if (idxU > -1) {
            wizardState.data.highlighting.useful.splice(idxU, 1);
            wizardState.data.usefulDataTexts.splice(idxU, 1);
        }

        // Aplicar nuevo resaltado
        span.classList.add(`highlight-${mode}`);
        if (mode === 'question') {
            wizardState.data.highlighting.question.push(segmentIndex);
        } else if (mode === 'useful') {
            wizardState.data.highlighting.useful.push(segmentIndex);
            wizardState.data.usefulDataTexts.push(span.textContent.trim());
        }
    }
}

export function clearHighlights() {
    const spans = document.querySelectorAll('.highlight-segment');
    spans.forEach(span => {
        span.classList.remove('highlight-question', 'highlight-useful');
    });

    wizardState.data.highlighting = { question: [], useful: [] };
    wizardState.data.usefulDataTexts = [];

    if (window.AudioService) {
        window.AudioService.speak('BORRAR SUBRAYADO', window.currentLanguage);
    }
}

export function validateHighlighting() {
    const problem = store.state.problems[store.state.currentProblem];
    const statementText = getProblemStatement(problem);

    // Ensure errorCounts is defined defensively
    if (!wizardState.data.errorCounts) {
        wizardState.data.errorCounts = {};
    }

    // Initialize error counter if not exists
    if (typeof wizardState.data.errorCounts.step2 === 'undefined') {
        wizardState.data.errorCounts.step2 = 0;
    }

    // Re-parse to match state
    const segments = parseTextIntoSegments(statementText);
    const highlightedAsQuestion = wizardState.data.highlighting.question;
    const highlightedAsUseful = wizardState.data.highlighting.useful;

    // Check 1: Is the question highlighted?
    // User must highlight any segment that is a question
    const questionSegments = segments
        .map((seg, idx) => ({ ...seg, idx }))
        .filter(seg => seg.type === 'question');

    // Relaxed check: Just verify they highlighted AT LEAST ONE question segment
    const allQuestionsHighlighted = questionSegments.length > 0 && 
                                    questionSegments.some(seg => highlightedAsQuestion.includes(seg.idx));

    // Check 2: Are the useful data highlighted correctly?
    const usefulNumbers = problem.data.useful;
    const distractorNumbers = problem.data.distractors || [];

    // Extract numbers from segments highlighted as useful
    // Note: A segment is a full phrase now, so it might contain multiple numbers.
    let highlightedNumbers = [];
    highlightedAsUseful.forEach(idx => {
        const segment = segments[idx];
        if (!segment) return;
        // Find all digits in this segment
        const matches = segment.text.match(/\d+/g);
        if (matches) {
            highlightedNumbers.push(...matches.map(n => parseInt(n)));
        }
    });

    // Check if all useful numbers are highlighted
    const allUsefulHighlighted = usefulNumbers.every(num =>
        highlightedNumbers.includes(num)
    );

    // Check if any distractors are wrongly highlighted
    const hasDistractorsHighlighted = highlightedNumbers.some(num =>
        distractorNumbers.includes(num)
    );

    // Validation logic with specific feedback
    if (!allQuestionsHighlighted || !allUsefulHighlighted || hasDistractorsHighlighted) {
        wizardState.data.errorCounts.step2 += 1;
        
        let feedbackMsg = window.getFeedbackForAttempt ? 
            window.getFeedbackForAttempt('step2', wizardState.data.errorCounts.step2, problem) : 
            'Revisa bien los datos y la pregunta.';
            
        // Provide audio and visual feedback
        if (window.DetectiveMentor && typeof window.DetectiveMentor.speak === 'function') {
            window.DetectiveMentor.speak(feedbackMsg, 'warning', 6000);
        } else if (window.showWizardFeedback) {
            window.showWizardFeedback(feedbackMsg, false, false, () => clearHighlights(true));
        }
        
        // Clear highlights after a delay
        setTimeout(() => clearHighlights(true), 1500);
        return;
    }

    // SUCCESS PATH
    wizardState.data.errorCounts.step2 = 0; // Reset counter on success

    // Extract the ACTUAL highlighted text segments that the user marked as "useful"
    // Since we use phrase blocks, we just take the raw text that contains the number
    
    const autoPhrases = window.getAllDataPhrases ? window.getAllDataPhrases(problem) : [];
    
    const usefulDataTexts = usefulNumbers.map((num, idx) => {
        const segmentIdx = highlightedAsUseful.find(highlightIdx => {
            const segment = segments[highlightIdx];
            if (segment && segment.text) {
                const matches = segment.text.match(/\d+/g) || [];
                return matches.some(m => parseInt(m) === num);
            }
            return false;
        });

        if (segmentIdx !== undefined && segments[segmentIdx]) {
            // Priority: use the cleanly extracted phrase (e.g. "22 sandías") instead of the whole raw segment
            if (autoPhrases[idx]) {
                return autoPhrases[idx];
            }

            // Fallback: Eliminar conectores irrelevantes del principio/final para que el "dato" no quede demasiado largo
            let extracted = segments[segmentIdx].text.trim();
            extracted = extracted.replace(/^[.,;:yYoO]+\s*/, '').replace(/[.,;:yYoO]+$/, '');
            return extracted;
        }

        // Fallback: reconstruct
        const objectNames = window.getAllObjectNames ? window.getAllObjectNames(problem) : [];
        const numIdx = usefulNumbers.indexOf(num);
        const objName = objectNames[numIdx] || 'unidades';
        return `${num} ${objName}`;
    });

    // Store for next steps
    wizardState.data.usefulDataTexts = usefulDataTexts;

    window.showWizardFeedback(getString('feedbackVeryGood'), true, true);
    if (window.playSuccess) window.playSuccess();
    
    window.markStepComplete(2);

    setTimeout(() => {
        window.routeNextWizardStep(2);
    }, 1500);
}
